import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Col,
  Row,
  Card,
  Label,
  Container,
} from 'reactstrap';
import { DatePicker } from 'antd';

import AppInputValidation from 'components/App/InputValidation';
import AppSelectValidation from 'components/App/SelectValidation';
import Breadcrumb from 'components/App/Breadcrumb';
import Meta from 'components/App/Meta';
import Button from 'components/App/Button';
import Loading from 'components/App/Loading';
import DataTableOperation from 'components/App/DataTableOperation';
import Pagination from 'components/Pagination';
import { LOCATIONS, themeColor } from 'constants/index';
import DownloadIcon from 'assets/images/download-white.svg';
import SearchIcon from 'assets/images/search-icon.svg';
import cn from 'utils/classnames';

import styles from './ManagerOperation.module.scss';

const PAGE_TITLE = 'QUẢN LÝ THAO TÁC';
const DEFAULT_LIMIT = 20;
const OPERATOR_LIST_LIMIT = 100;
const DATE_FORMAT = 'YYYY-MM-DD';

const FILTER_KEYS = ['q', 'module', 'adminId', 'actionType', 'from', 'to'];

const MODULE_OPTIONS = [
  { value: 'PLACE', label: 'Chỗ nghỉ' },
  { value: 'INTRANET_ADMINISTRATOR', label: 'Quản lý tài khoản người dùng' },
  { value: 'EXTRANET_ADMINISTRATOR', label: 'Tài khoản chỗ nghỉ' },
  { value: 'USER', label: 'Khách hàng' },
  { value: 'BOOKING', label: 'Đặt phòng' },
  { value: 'BOOKING_REVIEW', label: 'Đánh giá đặt phòng' },
  { value: 'REVENUE', label: 'Doanh thu' },
  { value: 'RANKING', label: 'Xếp hạng' },
  { value: 'FINANCE', label: 'Tài chính' },
];

const ACTION_TYPE_OPTIONS = [
  { value: 'CREATE', label: 'Thêm' },
  { value: 'UPDATE', label: 'Sửa' },
  { value: 'DELETE', label: 'Xoá' },
];

const breadcrumbList = [
  {
    name: 'Quản lý thao tác',
    isActive: true,
    link: LOCATIONS.MANAGER_OPERATION,
  },
];

function ManagerOperation({
  actionHistories,
  totalActionHistories,
  isLoadingGetActionHistories,
  isLoadingExportActionHistories,
  internalAccountList,
  getActionHistories,
  exportActionHistories,
  getInternalAccountList,
}) {
  const history = useHistory();

  const urlParams = useMemo(
    () => new URLSearchParams(history.location.search),
    [history.location.search],
  );

  const urlPage = useMemo(
    () => Number(urlParams.get('page') || 1),
    [urlParams],
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      q: urlParams.get('q') || '',
      module: urlParams.get('module') || '',
      adminId: urlParams.get('adminId') || '',
      actionType: urlParams.get('actionType') || '',
      from: urlParams.get('from') || '',
      to: urlParams.get('to') || '',
    },
    onSubmit: (values) => {
      const searchParams = new URLSearchParams(history.location.search);
      searchParams.set('page', '1');

      FILTER_KEYS.forEach((key) => {
        const value = key === 'q' ? values[key].trim() : values[key];

        if (value !== '' && value != null) {
          searchParams.set(key, value);
        } else {
          searchParams.delete(key);
        }
      });

      history.push(`?${searchParams.toString()}`);
    },
  });

  const totalPage = useMemo(
    () => Math.ceil(totalActionHistories / DEFAULT_LIMIT) || 1,
    [totalActionHistories],
  );

  const operationCountLabel = useMemo(
    () => new Intl.NumberFormat('vi-VN').format(totalActionHistories),
    [totalActionHistories],
  );

  const operatorOptions = useMemo(
    () => internalAccountList.map((account) => ({
      value: account._id,
      label: account.fullName || account.username || account.email,
    })),
    [internalAccountList],
  );

  useEffect(() => {
    getInternalAccountList({ page: 1, limit: OPERATOR_LIST_LIMIT });
  }, [getInternalAccountList]);

  useEffect(() => {
    const payload = {
      page: urlPage,
      limit: DEFAULT_LIMIT,
    };

    FILTER_KEYS.forEach((key) => {
      const value = urlParams.get(key);

      if (value !== '' && value != null) {
        payload[key] = value;
      }
    });

    getActionHistories(payload);
  }, [getActionHistories, urlPage, urlParams]);

  const onResetForm = useCallback(() => {
    validation.resetForm();
    history.push('?page=1');
  }, [validation, history]);

  const onChangeFilterOption = useCallback(
    (e) => {
      validation.setFieldValue(e.name, e.value, true);
    },
    [validation],
  );

  const onChangeDateRange = useCallback(
    (dates) => {
      validation.setFieldValue('from', dates?.[0]?.format(DATE_FORMAT) || '', false);
      validation.setFieldValue('to', dates?.[1]?.format(DATE_FORMAT) || '', false);
    },
    [validation],
  );

  const dateRangeValue = useMemo(() => {
    if (!validation.values.from && !validation.values.to) {
      return null;
    }

    return [
      validation.values.from ? dayjs(validation.values.from, DATE_FORMAT) : null,
      validation.values.to ? dayjs(validation.values.to, DATE_FORMAT) : null,
    ];
  }, [validation.values.from, validation.values.to]);

  const onChangePage = useCallback(
    (selected) => {
      const searchParams = new URLSearchParams(history.location.search);
      searchParams.set('page', String(selected.selected + 1));
      history.push(`?${searchParams.toString()}`);
    },
    [history],
  );

  const onClickExportFile = useCallback(() => {
    const payload = {};

    FILTER_KEYS.forEach((key) => {
      const value = urlParams.get(key);

      if (value !== '' && value != null) {
        payload[key] = value;
      }
    });

    exportActionHistories(payload);
  }, [exportActionHistories, urlParams]);

  return (
    <div className={cn('page-content', styles.Page)}>
      <Meta name={PAGE_TITLE} />

      <Container fluid>
        <Breadcrumb pageTitle={PAGE_TITLE} breadcrumbList={breadcrumbList} />

        <Card className="Section">
          <form onSubmit={validation.handleSubmit}>
            <Row>
              <Col xxl="6">
                <Label className="Section__Search__Label">
                  Bộ lọc
                </Label>

                <Row>
                  <Col md="6" className="mb-24">
                    <AppSelectValidation
                      name="module"
                      isHiddenSearchable
                      validation={validation}
                      placeholder="Module"
                      options={MODULE_OPTIONS}
                      value={validation.values.module}
                      onChange={onChangeFilterOption}
                      selectedClassName={styles.FilterSelect}
                    />
                  </Col>

                  <Col md="6" className="mb-24">
                    <AppSelectValidation
                      name="adminId"
                      isHiddenSearchable
                      validation={validation}
                      placeholder="Người thao tác"
                      options={operatorOptions}
                      value={validation.values.adminId}
                      onChange={onChangeFilterOption}
                      selectedClassName={styles.FilterSelect}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md="4" className="mb-24">
                    <AppSelectValidation
                      name="actionType"
                      isHiddenSearchable
                      validation={validation}
                      placeholder="Hành động"
                      options={ACTION_TYPE_OPTIONS}
                      value={validation.values.actionType}
                      onChange={onChangeFilterOption}
                      selectedClassName={styles.FilterSelect}
                    />
                  </Col>

                  <Col md="8" className={cn('mb-24', styles.FilterDateCol)}>
                    <DatePicker.RangePicker
                      className={styles.FilterDatePicker}
                      value={dateRangeValue}
                      onChange={onChangeDateRange}
                      format="DD/MM/YYYY"
                      placeholder={['Từ ngày', 'Đến ngày']}
                      allowClear
                    />
                  </Col>
                </Row>
              </Col>

              <Col xxl="6" className="mb-24">
                <Label className="Section__Search__Label">
                  Tìm kiếm
                </Label>

                <div className="Section__Search__Container">
                  <AppInputValidation
                    name="q"
                    id="q"
                    type="text"
                    placeholder="Tìm theo mã chỗ nghỉ, tên chỗ nghỉ..."
                    validation={validation}
                    inputClassName={cn('Section__Search__Input')}
                  />

                  <img
                    className="fas fa-search Section__Search__Icon__Search"
                    src={SearchIcon}
                    alt="search"
                  />
                </div>
              </Col>
            </Row>

            <div className="d-flex">
              <Button
                outline
                classes="Section__Search__Button__ReType mr-24"
                color={themeColor.LIGHT}
                onClick={onResetForm}
              >
                Nhập lại
              </Button>

              <Button
                classes="Section__Search__Button__Search"
                type="submit"
              >
                Tìm kiếm
              </Button>
            </div>
          </form>
        </Card>

        <Card className="Section">
          <Loading loading={isLoadingGetActionHistories} loaderOnPage />

          <div className="Tab__Header__Container">
            <Label className="Table__Body__Total">
              {`${operationCountLabel} thao tác`}
            </Label>

            <Button
              fontSize="13px"
              classes="Table__Header__Button"
              color={themeColor.LIGHT}
              onClick={onClickExportFile}
              disabled={isLoadingExportActionHistories}
            >
              <img src={DownloadIcon} alt="download icon" />
              Xuất file
            </Button>
          </div>

          <div className="Table__Body">
            <DataTableOperation operations={actionHistories} />
          </div>
        </Card>

        <Pagination
          page={urlPage}
          pageCount={totalPage}
          onChangePage={onChangePage}
        />
      </Container>
    </div>
  );
}

ManagerOperation.propTypes = {
  actionHistories: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    operatorName: PropTypes.string,
    category: PropTypes.string,
    action: PropTypes.string,
    description: PropTypes.string,
    changedAt: PropTypes.string,
  })),
  totalActionHistories: PropTypes.number,
  isLoadingGetActionHistories: PropTypes.bool,
  isLoadingExportActionHistories: PropTypes.bool,
  internalAccountList: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string,
    fullName: PropTypes.string,
    username: PropTypes.string,
    email: PropTypes.string,
  })),
  getActionHistories: PropTypes.func.isRequired,
  exportActionHistories: PropTypes.func.isRequired,
  getInternalAccountList: PropTypes.func.isRequired,
};

ManagerOperation.defaultProps = {
  actionHistories: [],
  totalActionHistories: 0,
  isLoadingGetActionHistories: false,
  isLoadingExportActionHistories: false,
  internalAccountList: [],
};

export default ManagerOperation;
